import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ServiceFeeType } from '@modeso/types__dgoods-products';
import { combineLatest, filter, Observable, Subject, takeUntil } from 'rxjs';
import { ProductSettingsDTO, ProductType } from '../../../../../dgoods-lib-products-fe/src/lib';
import { ViewProductSetting } from '../../shared/models/interfaces/ViewProductSetting.interface';
import { serviceFeeConstants } from '../fraudRules/constants';

@Component({
    selector: 'app-settings-edit-dialog',
    templateUrl: 'confirmation-dialog.component.html',
    styleUrls: ['confirmation-dialog.component.scss'],
})
export class SettingsEditDialogComponent implements OnInit, OnDestroy {
    readonly HTML_IS_NOT_ALLOWED = serviceFeeConstants.HTML_IS_NOT_ALLOWED;
    readonly productTypes = ProductType;
    readonly couponsEnabled = 'couponsEnabled';
    readonly displayOnIOS = 'displayOnIOS';
    readonly displayOnAndroid = 'displayOnAndroid';
    readonly isGreyedOut = 'isGreyedOut';
    readonly serviceFee = 'serviceFee';

    readonly editableSettings: readonly string[] = [
        this.couponsEnabled,
        this.displayOnIOS,
        this.displayOnAndroid,
        this.isGreyedOut,
        this.serviceFee,
    ];
    readonly updatedProduct: ProductSettingsDTO;
    displayNames: Record<string, string> = {
        [this.couponsEnabled]: 'Coupons Enabled',
        [this.displayOnIOS]: 'Display On IOS',
        [this.displayOnAndroid]: 'Display On Android',
        [this.isGreyedOut]: 'Gray out product',
        [this.serviceFee]: 'Service Fee',
    };

    serviceFeeForm?: FormGroup;
    serviceFeeType = ServiceFeeType;
    isError = false;
    errorMessage?: string;
    readonly isProductWhitelisted: boolean;
    private readonly destroyed$: Subject<boolean> = new Subject();
    private readonly error$: Observable<HttpErrorResponse | null>;
    private readonly success$: Observable<boolean>;
    private readonly PERCENTAGE_VALIDATION = [
        Validators.required,
        this.invalidNumberValidator,
        this.twoDecimalValidator,
        Validators.min(0),
        Validators.max(100),
    ];

    private readonly FIXED_AMOUNT_VALIDATION = [
        Validators.required,
        this.invalidNumberValidator,
        this.twoDecimalValidator,
        Validators.min(0),
    ];
    private readonly MAX_TEXT_LENGTH = 35;
    private readonly TEXT_VALIDATION = [
        this.notOnlyWhitespaceValidator,
        Validators.maxLength(this.MAX_TEXT_LENGTH),
        Validators.required,
    ];

    private readonly updateProductSettings: (data: ProductSettingsDTO) => void;

    constructor(
        private readonly dialogRef: MatDialogRef<SettingsEditDialogComponent>,
        private readonly fb: FormBuilder,
        @Inject(MAT_DIALOG_DATA) readonly data: ViewProductSetting,
    ) {
        this.updateProductSettings = data.updateProductSettings;
        this.error$ = data.error;
        this.success$ = data.success;
        this.isProductWhitelisted = data.whiteListed;
        this.updatedProduct = {
            productLineName: data.productLineName,
            couponsEnabled: data.couponsEnabled,
            displayOnAndroid: data.displayOnAndroid,
            displayOnIOS: data.displayOnIOS,
            isGreyedOut: data.isGreyedOut,
            type: data.type,
            serviceFee: data.serviceFee,
            serviceFeeConfig: data.serviceFeeConfig,
        };
    }

    public get feeType(): AbstractControl | undefined {
        return this.serviceFeeForm?.get('type');
    }

    public get amount(): AbstractControl | undefined {
        return this.serviceFeeForm?.get('amount');
    }

    public get text(): AbstractControl | undefined {
        return this.serviceFeeForm?.get('text');
    }

    ngOnInit(): void {
        this.initializeForm();
        this.setAmountValidators(this.feeType.value);
        this.dialogRequestResults();

        this.feeType.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value) => this.setAmountValidators(value));

        if (this.isProductWhitelisted) {
            this.serviceFeeForm.disable();
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    onYesClick(): void {
        this.isError = false;
        const newProduct: ProductSettingsDTO = { ...this.updatedProduct };

        if (this.updatedProduct.serviceFee) {
            newProduct.serviceFeeConfig = { ...this.serviceFeeForm.value };
        }

        if (this.isInternationalMobileTopUp()) {
            newProduct.serviceFee = false;
            newProduct.couponsEnabled = false;
            delete newProduct.serviceFeeConfig;
        }
        this.updateProductSettings(newProduct);
    }

    onNoClick(): void {
        this.dialogRef.close(null);
    }

    isInternationalMobileTopUp(): boolean {
        return this.updatedProduct.type === ProductType.TOP_UP;
    }

    private twoDecimalValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null || value === '') {
            return null;
        }

        const valueStr = value.toString();

        if (valueStr.includes('.')) {
            const decimalPart = valueStr.split('.')[1];
            const valid = /^\d+(\.\d{1,2})?$/.test(value);
            if (decimalPart.length > 2 && !valid) {
                return { decimals: 'Only 2 decimal places allowed' };
            }
        }
        return null;
    }

    private notOnlyWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
        const isWhitespace = (control.value ?? '').trim().length === 0;
        return isWhitespace ? { whitespace: true } : null;
    }

    private invalidNumberValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (value == null || value === '') {
            return null;
        }

        if (typeof value !== 'number' && typeof value !== 'string') {
            return { invalidNumber: true };
        }

        let num: number;

        if (typeof value === 'string') {
            // Reject leading/trailing whitespace
            if (value.length !== value.trim().length) {
                return { invalidNumber: true };
            }

            // Reject scientific notation
            if (value.toLowerCase().includes('e')) {
                return { invalidNumber: true };
            }

            num = Number(value);
        } else {
            num = value;
        }

        // Reject non-finite values (NaN, Infinity)
        if (!Number.isFinite(num)) {
            return { invalidNumber: true };
        }

        // Reject unsafe numeric range
        if (num < Number.MIN_SAFE_INTEGER || num > Number.MAX_SAFE_INTEGER) {
            return { invalidNumber: true };
        }

        return null;
    }

    private initializeForm(): void {
        const serviceFeeConfig = this.updatedProduct.serviceFeeConfig;
        const type = serviceFeeConfig?.type ?? ServiceFeeType.FIXED;
        const amount = serviceFeeConfig?.amount ?? null;
        const text = serviceFeeConfig?.text ?? { 'de-ch': '', 'it-ch': '', 'fr-ch': '', 'en-us': '' };

        this.serviceFeeForm = this.fb.group({
            type: [type, Validators.required],
            amount: [amount, Validators.required],
            text: this.fb.group({
                'de-ch': [text['de-ch'], this.TEXT_VALIDATION],
                'fr-ch': [text['fr-ch'], this.TEXT_VALIDATION],
                'en-us': [text['en-us'], this.TEXT_VALIDATION],
                'it-ch': [text['it-ch'], this.TEXT_VALIDATION],
            }),
        });
    }

    private setAmountValidators(value: ServiceFeeType): void {
        const control = this.amount;

        if (control == null) {
            return;
        }
        control.clearValidators();

        const validators =
            value === ServiceFeeType.PERCENTAGE ? this.PERCENTAGE_VALIDATION : this.FIXED_AMOUNT_VALIDATION;

        control.setValidators(validators);
        control.updateValueAndValidity();
    }

    private dialogRequestResults(): void {
        combineLatest([this.error$, this.success$])
            .pipe(
                filter(([error, success]) => success && error == null),
                takeUntil(this.destroyed$),
            )
            .subscribe(() => this.dialogRef.close());

        this.error$
            .pipe(
                filter((error: HttpErrorResponse) => error != null),
                takeUntil(this.destroyed$),
            )
            .subscribe((error) => {
                this.isError = true;
                if (error.status.toString() === '409') {
                    this.errorMessage = `Cannot save service fee. The product has been published. Please refresh and unpublish the product to make changes.`;
                } else {
                    this.errorMessage = `Error occurred while updating the settings`;
                }
            });
    }
}
